<template>
  <div
    ref="wrapper"
    class="status-select"
    :class="{
      'status-select--open': isOpen,
      'status-select--disabled': disabled,
    }"
  >
    <button
      type="button"
      class="status-select__trigger"
      :disabled="disabled"
      :style="!optionsHaveIcons ? { borderColor: currentColor } : {}"
      @click="toggle"
    >
      <!-- Icona contestuale fissa (placeholder o selezionato senza icona propria) -->
      <i
        v-if="placeholderIcon && !(currentOption && currentOption.icon)"
        :class="'fa-solid ' + placeholderIcon"
        class="status-placeholder-single"
      ></i>
      <!-- Placeholder con icone multiple: nessuna selezione -->
      <span
        v-else-if="!currentOption && optionsHaveIcons"
        class="status-placeholder-icons"
      >
        <template v-for="(opt, i) in options" :key="opt.value">
          <i :class="'fa-solid ' + opt.icon"></i>
          <span v-if="i < options.length - 1" class="status-placeholder-sep"
            >/</span
          >
        </template>
      </span>
      <!-- Icona opzione selezionata -->
      <i
        v-else-if="currentOption && currentOption.icon"
        :class="'fa-solid ' + currentOption.icon"
      ></i>
      <!-- Dot (comportamento originale) -->
      <span
        v-else
        class="status-dot"
        :style="{ background: currentColor }"
      ></span>

      <span class="status-select__label">{{
        optionsHaveIcons && !currentOption ? "" : currentLabel
      }}</span>
      <i
        class="fa-solid"
        :class="isOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
      ></i>
    </button>
    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="dropdown"
        class="status-select__dropdown"
        :style="dropdownStyle"
      >
        <button
          v-for="opt in options"
          :key="String(opt.value)"
          type="button"
          class="status-select__option"
          :class="{ selected: isSelected(opt) }"
          :style="
            isSelected(opt) && opt.color
              ? { background: colorBg(opt.color) }
              : {}
          "
          :disabled="opt.disabled"
          @click="select(opt)"
        >
          <i v-if="opt.icon" :class="'fa-solid ' + opt.icon"></i>
          <span
            v-else
            class="status-dot"
            :style="{ background: opt.color }"
          ></span>
          <span>{{ opt.label }}</span>
          <i
            v-if="isSelected(opt)"
            class="fa-solid fa-check status-select__check"
            :style="opt.color ? { color: opt.color } : {}"
          ></i>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script>
/** Quanto puo' allungarsi la tendina, la stessa `max-height` del foglio. */
const ALTEZZA_MASSIMA = 280;

export default {
  name: "StatusSelect",
  props: {
    modelValue: { default: null },
    options: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: "—" },
    placeholderIcon: { type: String, default: "" },
  },
  emits: ["update:modelValue"],
  data() {
    return { isOpen: false, dropdownStyle: {} };
  },
  computed: {
    currentOption() {
      return this.options.find((o) => o.value === this.modelValue);
    },
    currentLabel() {
      return this.currentOption?.label ?? this.placeholder;
    },
    currentColor() {
      return this.currentOption?.color ?? "#9ca3af";
    },
    optionsHaveIcons() {
      return this.options.some((o) => o.icon);
    },
  },
  beforeUnmount() {
    this.teardownListeners();
  },
  methods: {
    isSelected(opt) {
      return opt.value === this.modelValue;
    },
    toggle() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.updatePosition();
        this.$nextTick(this.updatePosition);
        setTimeout(() => {
          document.addEventListener("click", this.onClickOutside);
          window.addEventListener("resize", this.updatePosition);
          window.addEventListener("scroll", this.updatePosition, true);
        }, 0);
      } else {
        this.teardownListeners();
      }
    },
    updatePosition() {
      const trigger = this.$refs.wrapper;
      if (!trigger) return;
      const r = trigger.getBoundingClientRect();

      // [sc-48583] La tendina e' `fixed`: se esce dal bordo della finestra non
      // la allunga, e la pagina non ha niente da scorrere. Le voci sotto il
      // taglio non si raggiungono in nessun modo - e' quello che succedeva ai
      // sanitari, che sono nove. Quindi si guarda quanto spazio c'e' davvero:
      // se sotto non basta e sopra ce n'e' di piu', si apre verso l'alto, e in
      // ogni caso l'altezza si ferma dentro la finestra.
      const margine = 8;
      const sotto = window.innerHeight - r.bottom - margine;
      const sopra = r.top - margine;
      const inSu = sotto < ALTEZZA_MASSIMA && sopra > sotto;
      const spazio = Math.min(ALTEZZA_MASSIMA, inSu ? sopra : sotto);

      this.dropdownStyle = {
        position: "fixed",
        left: r.left + "px",
        minWidth: r.width + "px",
        // Sotto le centoventi la tendina non conterrebbe due voci intere: a
        // quel punto e' meglio che sbordi un poco, ma resti usabile.
        maxHeight: Math.max(120, spazio) + "px",
        ...(inSu
          ? { bottom: window.innerHeight - r.top + 4 + "px" }
          : { top: r.bottom + 4 + "px" }),
      };
    },
    teardownListeners() {
      document.removeEventListener("click", this.onClickOutside);
      window.removeEventListener("resize", this.updatePosition);
      window.removeEventListener("scroll", this.updatePosition, true);
    },
    select(opt) {
      if (opt.disabled) return;
      this.$emit("update:modelValue", opt.value);
      this.isOpen = false;
      this.teardownListeners();
    },
    colorBg(hex) {
      if (!hex) return "transparent";
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, 0.12)`;
    },
    onClickOutside(e) {
      const inWrapper = this.$refs.wrapper?.contains(e.target);
      const inDropdown = this.$refs.dropdown?.contains(e.target);
      if (!inWrapper && !inDropdown) {
        this.isOpen = false;
        this.teardownListeners();
      }
    },
  },
};
</script>

<style scoped>
.status-select {
  position: relative;
  display: inline-block;
}

.status-select__trigger {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  border-radius: 20px;
  border: 0.5px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 400;
  min-width: 108px;
  transition: border-color 0.15s;
}

.status-select__trigger:hover:not(:disabled) {
  border-color: #bbb;
}

.status-select--open .status-select__trigger {
  border-color: #999;
}

.status-select--disabled .status-select__trigger {
  opacity: 0.7;
  cursor: not-allowed;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-placeholder-single {
  font-size: 13px;
  color: #9ca3af;
}

.status-placeholder-icons {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #9ca3af;
}

.status-placeholder-icons i {
  font-size: 12px;
}

.status-placeholder-sep {
  font-size: 11px;
  color: #d1d5db;
  line-height: 1;
}

.status-select__label {
  flex: 1;
  text-align: left;
}

.status-select__trigger > i.fa-solid {
  font-size: 10px;
  color: #888;
}

.status-select__trigger > i:not(.fa-chevron-up):not(.fa-chevron-down) {
  font-size: 13px;
  color: #374151;
}

.status-select__dropdown {
  position: fixed;
  min-width: 100%;
  background: #fff;
  border: 0.5px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 2147483000;
  padding: 4px;
  max-height: 280px;
  overflow-y: auto;
}

.status-select__option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 400;
  text-align: left;
  transition: background 0.1s;
}

.status-select__option:hover:not(:disabled) {
  background: #f5f5f5;
}

.status-select__option:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.status-select__option i:not(.status-select__check) {
  font-size: 13px;
  color: #374151;
}

.status-select__check {
  margin-left: auto;
  font-size: 11px;
}
</style>
